import React, { useMemo } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { User } from '../../types';
import NotificationsPopover from './NotificationsPopover';
import UserAvatar from '../common/UserAvatar';
import { ChevronLeft } from 'lucide-react';
import { Logo } from '../common/Logo';

interface Props {
  user: User;
}

const MobileHeader: React.FC<Props> = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Define pages where we should NOT show the back button
  const rootPaths = ['/', '/dashboard', '/feed', '/news', '/leaderboard', '/admin'];
  const isRootPage = rootPaths.includes(location.pathname);

  const getPageTitle = (pathname: string): string => {
    if (matchPath('/', pathname) || matchPath('/dashboard', pathname)) return 'TechCoop';
    if (matchPath('/feed', pathname)) return 'Hoạt động';
    if (matchPath('/news', pathname)) return 'Tin tức';
    if (matchPath('/leaderboard', pathname)) return 'Bảng xếp hạng';
    if (matchPath('/admin/*', pathname)) return 'Quản trị';
    if (matchPath('/user/:userId', pathname)) return 'Hồ sơ người dùng';
    if (matchPath('/user/:userId/activity/:activityId', pathname)) return 'Chi tiết hoạt động';
    if (matchPath('/team/:teamId', pathname)) return 'Chi tiết đội';
    return isRootPage ? 'TechCoop' : '';
  };

  const pageTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname, isRootPage]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="md:hidden sticky top-0 z-40 w-full px-4 py-3 bg-white/70 backdrop-blur-xl border-b border-white/40 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] flex justify-between items-center transition-all duration-300">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {!isRootPage ? (
          <button
            onClick={handleBack}
            className="w-10 h-10 rounded-full flex items-center justify-center bg-white/50 backdrop-blur-md border border-white/60 shadow-sm active:scale-95 transition-all text-slate-700 hover:text-indigo-600 hover:bg-white/80 flex-shrink-0"
          >
            <ChevronLeft size={22} className="relative -left-0.5" />
          </button>
        ) : (
          <div className="flex-shrink-0">
            <Logo className="w-8 h-8 text-indigo-600/90 drop-shadow-sm" />
          </div>
        )}

        <span className={`font-bold text-lg tracking-tight truncate ${pageTitle === 'TechCoop' ? 'text-indigo-600/90' : 'text-slate-800'}`}>
          {pageTitle}
        </span>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        <div className="flex items-center bg-white/40 backdrop-blur-sm p-1.5 pr-2 rounded-full border border-white/60 shadow-sm">
          <NotificationsPopover userId={user.id} isMobile={true} />
          <div className="w-px h-6 bg-slate-200 mx-1"></div>
          <UserAvatar
            user={user}
            className="w-8 h-8 rounded-full border-2 border-white shadow-sm object-cover"
            alt="Profile"
          />
        </div>
      </div>
    </div>
  );
};

export default MobileHeader;
